<template>
    <div class="fixed pointer-events-none space-y-1.5">
        <TransitionGroup
            enter-from-class="opacity-0 translate-x-full"
            enter-to-class="opacity-100 translate-x-0"
            enter-active-class="transition-all duration-200 ease-in-out"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0 scale-[1.15]"
            leave-active-class="transition-all duration-200 ease-in-out"
            move-class="transition-all duration-200 ease-in-out"
        >
            <div
                v-for="notification in notifications"
                :key="notification.id"
                class="relative isolate flex bg-white dark:bg-logo-500 rounded-xl overflow-hidden shadow-hero pointer-events-auto"
            >
                <div
                    class="p-3 flex"
                    :class="[notification.color]"
                >
                    <Icon
                        :name="notification.icon"
                        class="m-auto w-6 h-6 text-white"
                    />
                </div>

                <div class="relative flex-1 min-w-0 p-4">
                    <div class="absolute top-2 right-2">
                        <button
                            class="button button-sm button-icon button-ternary"
                            @click="clear(notification)"
                        >
                            <Icon
                                name="icon-close"
                                class="w-6 h-6"
                            />
                        </button>
                    </div>

                    <h4 class="text-font-primary dark:text-font-light font-semibold leading-none">
                        {{ notification.title }}
                    </h4>

                    <div
                        class="mt-2 text-sm text-font-secondary dark:text-font-light leading-tight"
                        v-html="notification.text"
                    />
                </div>
            </div>
        </TransitionGroup>
    </div>
</template>

<script setup>
    import { ref, onBeforeUnmount, onMounted } from 'vue';
    import bus from '/src/utils/bus.js';

    defineOptions({
        name: 'AppNotifications',
    });

    const props = defineProps({
        duration: {
            type: Number,
            default: 5000,
        },
    });

    // notifications
    const notifications = ref([]);
    function add ({ title, text, type, duration = props.duration }) {
        const notification = {
            id: new Date().getTime(),
            title,
            text,
            duration,
            ...types[type],
        };

        if (duration > 0) {
            notification.timer = setTimeout(() => {
                clear(notification);
            }, duration);
        }

        notifications.value.push(notification);
    }
    function clear (notification) {
        clearTimeout(notification.timer);
        const index = notifications.value.indexOf(notification);

        if (index >= 0) {
            notifications.value.splice(index, 1);
        }
    }

    // types
    const types = {
        info: {
            icon: 'icon-info-circle',
            color: 'bg-aux-300',
        },
        warning: {
            icon: 'icon-alert-triangle',
            color: 'bg-feedback-warning',
        },
        error: {
            icon: 'icon-alert-circle',
            color: 'bg-feedback-error',
        },
        success: {
            icon: 'icon-check-circle',
            color: 'bg-feedback-success',
        },
    };

    // listeners
    onMounted(() => {
        bus.on('notifications:add', add);
        bus.on('notifications:clear', clear);
    });
    onBeforeUnmount(() => {
        bus.off('notifications:add', add);
        bus.off('notifications:clear', clear);
        notifications.value.forEach(n => clearTimeout(n.timer));
    });
</script>
